import React, {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useParams, useLocation} from 'react-router-dom'
import {Redirect} from 'react-router-dom'

import {Box, Theme, CircularProgress} from '@mui/material'
import {makeStyles} from 'tss-react/mui'

import {
  useConsultationsMyAppointmentsQuery,
  useConsultationsProgramDetailQuery,
} from '@progyny/generated/graphql'
import {useProductLine} from '@progyny/hooks/useProductLine'
import EmotionalWellBeingDisclaimerModal from '@progyny/pages/Consultations/Program/components/EmotionalWellBeingDisclaimerModal'
import Navigation from '@progyny/pages/Consultations/Program/components/Navigation'
import PastSessions, {
  PastAppointmentItem,
} from '@progyny/pages/Consultations/Program/components/PastSessions'
import ProgramOverviewCard from '@progyny/pages/Consultations/Program/components/ProgramOverviewCard'
import UpcomingSessions from '@progyny/pages/Consultations/Program/components/UpcomingSessions'
import CancelConsultation from '@progyny/pages/Consultations/components/CancelConsultation'
import type {SessionForBooking} from '@progyny/pages/Consultations/components/SingleSession/SingleSessionBooking'
import {useProgramEnrollment} from '@progyny/pages/Consultations/hooks/useProgramEnrollment'
import {
  filterPastAppointments,
  filterUpcomingAppointments,
  getAppointmentsBySlug,
} from '@progyny/utils/appointmentHelpers'

export interface SessionPlanProps {
  colors?: any
  programSlug?: string
  onCalendarVisibilityChange?: (visible: boolean) => void
  onTabsVisibilityChange?: (visible: boolean) => void
}

export const useStyles = makeStyles<{colors?: any}>()(
  (theme: Theme, {colors}) => ({
    container: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      gap: '24px',
    },
    pastAppointments: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    outlinedButton: {
      boxShadow: 'none !important',
      borderColor: `${colors?.primary || '#148199'} !important`,
      '&:hover': {
        boxShadow: 'none !important',
        borderColor: `${colors?.primary || '#148199'} !important`,
      },
      '&.Mui-disabled': {
        boxShadow: 'none !important',
        borderColor: '#D6D8E0 !important',
      },
    },
    header: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      alignItems: 'flex-start',
      justifyContent: 'center',
    },
    iconFrame: {
      boxSizing: 'border-box',
      width: '44px',
      height: '44px',
      background: colors?.secondary || '#F1FCFE',
      border: `1px solid ${colors?.primary || '#B3E1EA'}`,
      borderRadius: '1000px',
      flex: 'none',
      order: 0,
      flexGrow: 0,
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    icon: {
      width: '20px',
      height: '20px',
      flexShrink: 0,
    },
    title: {
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '24px',
      marginBottom: '8px',
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: 'row',
      gap: '12px',
      alignItems: 'center',
      flexShrink: 0,
    },
    consultationContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    contentSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '32px',
      width: '100%',
      justifyContent: 'center',
      '@container (min-width: 800px)': {
        flexDirection: 'row',
        alignItems: 'center',
      },
    },
    content: {
      width: '100%',
      maxWidth: '860px',
      '& ul': {
        paddingLeft: '18px',
        marginTop: 0,
      },
      '& p': {
        margin: '2px 0',
      },
    },
  })
)

interface ProgramParams {
  program: string
}

const SessionPlan: React.FC<SessionPlanProps> = ({
  colors,
  programSlug: programSlugProp,
  onCalendarVisibilityChange,
  onTabsVisibilityChange,
}) => {
  const {classes} = useStyles({colors})
  const {isFertility} = useProductLine()
  const {t} = useTranslation()
  const history = useHistory()
  const location = useLocation()

  const {program: programSlugFromUrl} = useParams<ProgramParams>()
  const programSlug = programSlugProp || programSlugFromUrl

  const {data, loading, error, refetch} = useConsultationsProgramDetailQuery({
    variables: {slug: programSlug},
    skip: !programSlug,
  })

  const {
    data: appointmentsData,
    loading: loadingAppointments,
    refetch: refetchAppointments,
  } = useConsultationsMyAppointmentsQuery({
    fetchPolicy: 'cache-first',
  })

  const program = data?.consultations.program
  const programEnrollment = data?.consultations.myProgramEnrollments?.find(
    (e) => e?.program?.id === program?.id
  )
  const isEnrolled = !!programEnrollment

  const {
    enrolling,
    requiresDisclaimer,
    showDisclaimerModal,
    setShowDisclaimerModal,
    handleEnroll,
    handleDisclaimerAcknowledge,
    handleDisclaimerClose,
  } = useProgramEnrollment({
    programId: program?.id ?? undefined,
    programSlug,
    isEnrolled,
    onSuccess: () => refetch(),
  })

  const programAppointments = getAppointmentsBySlug(
    programSlug,
    appointmentsData
  )
  const pastAppointments = filterPastAppointments(programAppointments)
  const upcomingAppointments = filterUpcomingAppointments(programAppointments)

  const sessions =
    program?.sessions?.map((session) => {
      const sessionStatus = programEnrollment?.sessionStatuses?.find(
        (ss) => ss?.session?.id === session?.id
      )
      const appointment = sessionStatus?.appointment
      const status = sessionStatus?.status

      let displayStatus:
        | 'completed'
        | 'upcoming'
        | 'readyForBooking'
        | 'locked'
        | 'notAttended'
        | 'skipped'
      if (status === 'COMPLETED') {
        displayStatus = 'completed'
      } else if (status === 'BOOKED') {
        displayStatus = 'upcoming'
      } else if (status === 'AVAILABLE') {
        displayStatus = 'readyForBooking'
      } else if (status === 'SKIPPED') {
        displayStatus = 'skipped'
      } else {
        displayStatus = 'locked'
      }

      return {
        sessionIndex: String(session?.sessionNumber || ''),
        title: session?.title!,
        subtitle: session?.subtitle || '',
        description: session?.description!,
        status: displayStatus,
        appointment: appointment,
        required: session?.required ?? true,
      }
    }) || []

  const hasUpcoming = upcomingAppointments.length > 0
  const hasPast = pastAppointments.length > 0
  const showNavigation = hasUpcoming || hasPast
  const [isCalendarVisible, setIsCalendarVisible] = React.useState(false)
  const isTabsVisible = !programSlugProp && showNavigation && !isCalendarVisible

  const handleCalendarVisibilityChange = (visible: boolean) => {
    setIsCalendarVisible(visible)
    onCalendarVisibilityChange?.(visible)
  }

  useEffect(() => {
    onTabsVisibilityChange?.(isTabsVisible)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabsVisible])

  const activeNavItem = React.useMemo(() => {
    if (location.pathname.endsWith('/upcoming')) return 'upcoming'
    if (location.pathname.endsWith('/past')) return 'past'
    return 'overview'
  }, [location.pathname])

  useEffect(() => {
    if (!loading && data?.consultations?.program && requiresDisclaimer) {
      const prog = data.consultations.program
      const enrolled = data.consultations.myProgramEnrollments?.find(
        (e) => e?.program?.id === prog.id
      )
      if (!enrolled) {
        setShowDisclaimerModal(true)
      }
    }
  }, [loading, data, requiresDisclaimer, setShowDisclaimerModal])

  const [activeCancelUrl, setActiveCancelUrl] = React.useState<string | null>(
    null
  )

  const handleBookingBack = React.useCallback(() => {
    refetch()
    refetchAppointments()
  }, [refetch, refetchAppointments])

  const handleCancelBack = React.useCallback(() => {
    setActiveCancelUrl(null)
    handleBookingBack()
  }, [handleBookingBack])

  React.useEffect(() => {
    if (activeNavItem !== 'upcoming' && activeCancelUrl) {
      setActiveCancelUrl(null)
    }
  }, [activeNavItem, activeCancelUrl])

  useEffect(() => {
    refetch()
  }, [location.pathname, refetch])

  useEffect(() => {
    if (
      programSlug &&
      !showNavigation &&
      (activeNavItem === 'upcoming' || activeNavItem === 'past')
    ) {
      history.replace(
        `/consultations/program/${programSlug}${isFertility ? '/upcoming' : ''}`
      )
    }
  }, [programSlug, showNavigation, activeNavItem, history, isFertility])

  useEffect(() => {
    if (
      !loading &&
      !loadingAppointments &&
      programSlug &&
      isFertility &&
      hasUpcoming &&
      activeNavItem === 'overview'
    ) {
      history.replace(`/consultations/program/${programSlug}/upcoming`)
    }
  }, [
    loading,
    loadingAppointments,
    programSlug,
    isFertility,
    hasUpcoming,
    activeNavItem,
    history,
  ])

  if (loading || loadingAppointments) {
    return (
      <Box minHeight={140}>
        <CircularProgress color={'primary'} size={32} />
      </Box>
    )
  }

  if (error || !data?.consultations?.program) {
    return null
  }

  const canEnrollInPrograms =
    program?.consultation?.canEnrollInPrograms ?? false
  const showBookingButton =
    isEnrolled && program?.bookingStrategy !== 'PRE_BOOK_ALL'
  const enableLearnMore = program?.enableLearnMoreContent ?? true

  const handleNavItemClick = (itemID: string) => {
    const basePath = `/consultations/program/${programSlug}`
    switch (itemID) {
      case 'upcoming':
        history.push(`${basePath}/upcoming`)
        break
      case 'past':
        history.push(`${basePath}/past`)
        break
      case 'overview':
      default:
        history.push(basePath)
        break
    }
  }

  const overviewItem = {
    itemID: 'overview',
    label: isFertility
      ? t('profile_member_status_book_consultation')
      : t('consultations_program_session_plan_consultation_overview'),
  }
  const upcomingTabButton = {
    itemID: 'upcoming',
    label: t('consultations_program_session_plan_upcoming_consultations'),
  }
  const pastTabButton = {
    itemID: 'past',
    label: t('consultations_program_session_plan_past_consultations'),
  }
  const showOverviewNav = !isFertility
  const navigationItems = [
    ...(showOverviewNav ? [overviewItem] : []),
    ...(hasUpcoming || isFertility ? [upcomingTabButton] : []),
    ...(hasPast ? [pastTabButton] : []),
  ]

  const sessionRaw = program?.sessions?.find((s) => s?.sessionNumber === 1)
  const session: SessionForBooking | null = sessionRaw
    ? {
        id: sessionRaw.id!,
        calcomEventType: sessionRaw.calcomEventType,
        title: sessionRaw.title,
        subtitle: sessionRaw.subtitle,
        description: sessionRaw.description,
        bookingDescription: sessionRaw.bookingDescription,
      }
    : null

  const renderContent = () => {
    if (activeCancelUrl && activeNavItem !== 'past') {
      return (
        <CancelConsultation
          cancelUrl={activeCancelUrl}
          onBack={handleCancelBack}
        />
      )
    }

    switch (activeNavItem) {
      case 'overview':
        return isFertility ? (
          <Redirect to='/consultations/program/fertility-consultation/upcoming' />
        ) : (
          <ProgramOverviewCard
            programSlug={programSlug}
            programDescription={program?.description}
            bookingStrategy={program?.bookingStrategy}
            isEnrolled={isEnrolled}
            canEnrollInPrograms={canEnrollInPrograms}
            enableLearnMore={enableLearnMore}
            showBookingButton={showBookingButton}
            sessions={sessions}
            colors={colors}
            enrolling={enrolling}
            onEnroll={handleEnroll}
            onCancelRequested={(url) => setActiveCancelUrl(url)}
          />
        )

      case 'upcoming':
        return (
          <UpcomingSessions
            sessions={sessions.filter((s) => s.status === 'upcoming')}
            colors={colors}
            programSlug={programSlug}
            t={t}
            hasPast={hasPast}
            onCancelRequested={(url) => setActiveCancelUrl(url)}
            session={session}
            onCalendarVisibilityChange={handleCalendarVisibilityChange}
            onBookingBack={handleBookingBack}
          />
        )

      case 'past':
        return (
          <PastSessions
            appointments={
              isFertility
                ? (pastAppointments as PastAppointmentItem[])
                : undefined
            }
            sessions={
              !isFertility
                ? sessions.filter((s) => s.status === 'completed')
                : undefined
            }
            colors={colors}
            className={classes.pastAppointments}
          />
        )

      default:
        return null
    }
  }

  return (
    <Box className={classes.container}>
      {isTabsVisible && (
        <Navigation
          items={navigationItems}
          activeItemID={activeNavItem}
          onItemClick={handleNavItemClick}
          colors={colors}
        />
      )}

      {renderContent()}

      {requiresDisclaimer && (
        <EmotionalWellBeingDisclaimerModal
          open={showDisclaimerModal}
          onClose={handleDisclaimerClose}
          onAcknowledge={handleDisclaimerAcknowledge}
          loading={enrolling}
        />
      )}
    </Box>
  )
}

export default SessionPlan
